import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import type { Receipt, AllocationStatus, PaymentMethod } from "@/types/payments";
import { generateMockReceipts } from "@/data/payments-mock-data";
import {
  type PaginationResult,
  type GraphQLConnection,
  pageToCursor,
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  createDefaultPagination,
  computePaginationWithCount,
} from "@/lib/graphql-pagination";

export type { PaginationResult } from "@/lib/graphql-pagination";

// --- GraphQL types ---

export interface ReceiptGraphQLNode {
  transactionId: number;
  referenceNumber: string | null;
  customerId: number;
  customerName: string | null;
  accountNumber: string | null;
  creditControllerId: number | null;
  transactionDate: string;
  amount: number;
  outstandingAmount: number;
  totalAllocated: number;
  currencyCode: string | null;
  paymentMethodId: number | null;
  paymentMethodCode: string | null;
  paymentMethodName: string | null;
  transactionReference: string | null;
  bankReference: string | null;
  allocationStatusCode: string | null;
  allocationStatusName: string | null;
  allocationCount: number;
  externalId: string | null;
  importedAt: string | null;
  importBatchId: string | null;
  transactionNotes: string | null;
  currencyAmount: number | null;
  currencyOutstandingAmount: number | null;
  hasPromise: boolean;
  onDispute: boolean;
  hasDispute: boolean;
  createdByName: string | null;
  transactionCreatedAt: string;
  modifiedByName: string | null;
  transactionModifiedAt: string | null;
}

export interface ReceiptListGraphQLResponse {
  receipts: {
    receiptList: GraphQLConnection<ReceiptGraphQLNode>;
  };
}

interface ReceiptCountGraphQLResponse {
  receipts: {
    receiptCount: number;
  };
}

export interface ReceiptTotalsData {
  totalCount: number;
  totalAmount: number;
  totalOutstanding: number;
  unallocatedCount: number;
  unallocatedAmount: number;
  fullyAllocatedCount: number;
  fullyAllocatedAmount: number;
  partiallyAllocatedCount: number;
  partiallyAllocatedAmount: number;
}

export interface ReceiptTotalsGraphQLResponse {
  receipts: {
    receiptTotals: ReceiptTotalsData;
  };
}

// --- GraphQL queries ---

const RECEIPT_NODE_FIELDS = `
  transactionId
  referenceNumber
  customerId
  customerName
  accountNumber
  creditControllerId
  transactionDate
  amount
  outstandingAmount
  totalAllocated
  currencyCode
  paymentMethodId
  paymentMethodCode
  paymentMethodName
  transactionReference
  bankReference
  allocationStatusCode
  allocationStatusName
  allocationCount
  externalId
  importedAt
  importBatchId
  transactionNotes
  currencyAmount
  currencyOutstandingAmount
  hasPromise
  onDispute
  hasDispute
  createdByName
  transactionCreatedAt
  modifiedByName
  transactionModifiedAt
`;

const RECEIPT_LIST_QUERY = `
  query ReceiptList($first: Int, $after: String, $sort: [ReceiptListSortSortByInput!], $filter: ReceiptListFilterInput) {
    receipts {
      receiptList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            ${RECEIPT_NODE_FIELDS}
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const RECEIPT_COUNT_QUERY = `
  query ReceiptCount($filter: ReceiptListFilterInput) {
    receipts {
      receiptCount(filter: $filter)
    }
  }
`;

export const RECEIPT_TOTALS_QUERY = `
  query ReceiptTotals($filter: ReceiptListFilterInput) {
    receipts {
      receiptTotals(filter: $filter) {
        totalCount
        totalAmount
        totalOutstanding
        unallocatedCount
        unallocatedAmount
        fullyAllocatedCount
        fullyAllocatedAmount
        partiallyAllocatedCount
        partiallyAllocatedAmount
      }
    }
  }
`;

// --- Mapping ---

function mapAllocationStatusCode(code: string | null): AllocationStatus {
  switch (code) {
    case "fully_allocated":
      return "fully_allocated";
    case "partially_allocated":
      return "partially_allocated";
    case "unallocated":
      return "unallocated";
    case "over_allocated":
      return "over_allocated";
    default:
      return "unallocated";
  }
}

function mapPaymentMethodCode(code: string | null): PaymentMethod {
  switch (code) {
    case "bank_transfer":
      return "bank_transfer";
    case "credit_card":
      return "credit_card";
    case "direct_debit":
      return "direct_debit";
    case "cheque":
      return "cheque";
    case "cash":
      return "cash";
    default:
      return "bank_transfer";
  }
}

export function mapGraphQLNodeToReceipt(node: ReceiptGraphQLNode, homeCurrency?: string): Receipt {
  const home = homeCurrency || "GBP";
  const isNonHomeCurrency = node.currencyCode != null && node.currencyCode !== home;
  return {
    id: node.transactionId,
    receiptNumber: node.referenceNumber || String(node.transactionId),
    customerId: node.customerId,
    customerName: node.customerName || "",
    customerAccountNumber: node.accountNumber || "",
    receiptDate: node.transactionDate ? new Date(node.transactionDate) : new Date(),
    receivedAmount: isNonHomeCurrency ? (node.currencyAmount ?? 0) : node.amount,
    allocatedAmount: node.totalAllocated,
    unappliedAmount: isNonHomeCurrency ? (node.currencyOutstandingAmount ?? 0) : node.outstandingAmount,
    currency: node.currencyCode || home,
    homeReceivedAmount: isNonHomeCurrency ? node.amount : undefined,
    homeUnappliedAmount: isNonHomeCurrency ? node.outstandingAmount : undefined,
    paymentMethod: mapPaymentMethodCode(node.paymentMethodCode),
    paymentReference: node.transactionReference || "",
    bankReference: node.bankReference || undefined,
    externalId: node.externalId || "",
    importedAt: node.importedAt ? new Date(node.importedAt) : new Date(),
    importBatchId: node.importBatchId || undefined,
    notes: node.transactionNotes || undefined,
    allocationStatus: mapAllocationStatusCode(node.allocationStatusCode),
    hasPromise: node.hasPromise,
    createdBy: node.createdByName || "",
    createdAt: node.transactionCreatedAt ? new Date(node.transactionCreatedAt) : new Date(),
    modifiedBy: node.modifiedByName || undefined,
    modifiedAt: node.transactionModifiedAt ? new Date(node.transactionModifiedAt) : undefined,
  };
}

// --- Sort / Filter builders ---

export interface GetReceiptsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  currency?: string;
  paymentMethod?: string;
  allocationStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  hasPromise?: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const sortFieldMap: Record<string, string> = {
  receiptNumber: "ReferenceNumber",
  customerName: "CustomerName",
  receiptDate: "TransactionDate",
  receivedAmount: "Amount",
  unappliedAmount: "OutstandingAmount",
  allocationStatus: "AllocationStatusName",
  paymentMethod: "PaymentMethodName",
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: "asc" | "desc") {
  return buildSort(sortFieldMap, sortBy, sortOrder);
}

export function buildGraphQLFilterVariable(params: GetReceiptsParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.allocationStatus && params.allocationStatus !== "all") {
    filter.allocationStatusCode = { eq: params.allocationStatus };
  }
  if (params.paymentMethod && params.paymentMethod !== "all") {
    filter.paymentMethodCode = { eq: params.paymentMethod };
  }
  if (params.amountMin !== undefined) {
    filter.amount = { ...((filter.amount as Record<string, unknown>) ?? {}), ge: params.amountMin };
  }
  if (params.amountMax !== undefined) {
    filter.amount = { ...((filter.amount as Record<string, unknown>) ?? {}), le: params.amountMax };
  }
  if (params.dateFrom) {
    filter.transactionDate = { ...((filter.transactionDate as Record<string, unknown>) ?? {}), ge: params.dateFrom };
  }
  if (params.dateTo) {
    filter.transactionDate = { ...((filter.transactionDate as Record<string, unknown>) ?? {}), le: params.dateTo };
  }
  if (params.currency && params.currency !== "all") {
    filter.currencyCode = { eq: params.currency };
  }
  if (params.hasPromise !== undefined) {
    filter.hasPromise = { eq: params.hasPromise };
  }
  filter.onDispute = { eq: false };
  filter.hasDispute = { eq: false };

  return filter;
}

// --- Mock helpers ---

function getMockReceiptsFromStore(): Receipt[] {
  return generateMockReceipts();
}

function filterMockReceipts(receipts: Receipt[], params: GetReceiptsParams): Receipt[] {
  return receipts.filter((x) => {
    const matchesSearch = !params.search ||
      x.receiptNumber.toLowerCase().includes(params.search.toLowerCase()) ||
      x.customerName.toLowerCase().includes(params.search.toLowerCase()) ||
      x.customerAccountNumber.toLowerCase().includes(params.search.toLowerCase());

    const matchesStatus = !params.allocationStatus || params.allocationStatus === "all" || x.allocationStatus === params.allocationStatus;
    const matchesPaymentMethod = !params.paymentMethod || params.paymentMethod === "all" || x.paymentMethod === params.paymentMethod;

    return matchesSearch && matchesStatus && matchesPaymentMethod;
  });
}

// --- Result types ---

export interface ReceiptsListResult {
  receipts: Receipt[];
  pagination: PaginationResult;
}

// --- Hooks ---

function useReceiptCountGraphQL(params?: GetReceiptsParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  return useModeAwareQuery<number, Error>(
    ["receipts-graphql-count", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<ReceiptCountGraphQLResponse>(
        apiConfig,
        RECEIPT_COUNT_QUERY,
        variables,
        signal,
      );

      return response.receipts.receiptCount;
    },
    0,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

export function useReceiptsListGraphQL(params?: GetReceiptsParams) {
  const apiConfig = useApiConfig();
  const allMockReceipts = getMockReceiptsFromStore();
  const queryParams: GetReceiptsParams = { page: 1, pageSize: 20, sortBy: "receiptDate", sortOrder: "desc", ...params };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 20;
  const filteredMock = filterMockReceipts(allMockReceipts, queryParams);
  const mockPaged = filteredMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockPagination = computeMockPagination(filteredMock.length, mockPage, mockPageSize);
  const mockResult: ReceiptsListResult = { receipts: mockPaged, pagination: mockPagination };

  return useModeAwareQuery<ReceiptsListResult, Error>(
    ["receipts-graphql-list", queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 20;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<ReceiptListGraphQLResponse>(
        apiConfig,
        RECEIPT_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.receipts.receiptList;
      const receipts = connection.edges.map((x) => mapGraphQLNodeToReceipt(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: receipts.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { receipts, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

export function useReceiptTotalsGraphQL(params?: GetReceiptsParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  const allMockReceipts = getMockReceiptsFromStore();
  const filteredMock = filterMockReceipts(allMockReceipts, params ?? {});

  const mockTotals: ReceiptTotalsData = {
    totalCount: filteredMock.length,
    totalAmount: filteredMock.reduce((sum, x) => sum + x.receivedAmount, 0),
    totalOutstanding: filteredMock.reduce((sum, x) => sum + x.unappliedAmount, 0),
    unallocatedCount: filteredMock.filter((x) => x.allocationStatus === "unallocated").length,
    unallocatedAmount: filteredMock.filter((x) => x.allocationStatus === "unallocated").reduce((sum, x) => sum + x.receivedAmount, 0),
    fullyAllocatedCount: filteredMock.filter((x) => x.allocationStatus === "fully_allocated").length,
    fullyAllocatedAmount: filteredMock.filter((x) => x.allocationStatus === "fully_allocated").reduce((sum, x) => sum + x.receivedAmount, 0),
    partiallyAllocatedCount: filteredMock.filter((x) => x.allocationStatus === "partially_allocated").length,
    partiallyAllocatedAmount: filteredMock.filter((x) => x.allocationStatus === "partially_allocated").reduce((sum, x) => sum + x.receivedAmount, 0),
  };

  return useModeAwareQuery<ReceiptTotalsData, Error>(
    ["receipts-graphql-totals", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<ReceiptTotalsGraphQLResponse>(
        apiConfig,
        RECEIPT_TOTALS_QUERY,
        variables,
        signal,
      );

      return response.receipts.receiptTotals;
    },
    mockTotals,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

// --- Single receipt by ID hook ---

const RECEIPT_BY_ID_QUERY = `
  query ReceiptById($first: Int, $filter: ReceiptListFilterInput) {
    receipts {
      receiptList(first: $first, filter: $filter) {
        edges {
          node {
            ${RECEIPT_NODE_FIELDS}
          }
        }
      }
    }
  }
`;

export function useReceiptByIdGraphQL(receiptId: number | undefined, homeCurrency?: string) {
  const apiConfig = useApiConfig();
  const allMockReceipts = getMockReceiptsFromStore();
  const mockReceipt = allMockReceipts.find((r) => r.id === receiptId) ?? null;

  return useModeAwareQuery<Receipt | null, Error>(
    ["receipt-graphql-by-id", receiptId],
    async ({ signal }) => {
      if (!receiptId) return null;

      const variables = {
        first: 1,
        filter: { transactionId: { eq: receiptId } },
      };

      const response = await graphqlQuery<ReceiptListGraphQLResponse>(
        apiConfig,
        RECEIPT_BY_ID_QUERY,
        variables,
        signal,
      );

      const edges = response.receipts.receiptList.edges;
      if (edges.length === 0) return null;
      return mapGraphQLNodeToReceipt(edges[0].node, homeCurrency);
    },
    mockReceipt,
    {
      enabled: !!receiptId,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

// --- Composite page data hook ---

const defaultPagination = createDefaultPagination(25);

const defaultTotals: ReceiptTotalsData = {
  totalCount: 0,
  totalAmount: 0,
  totalOutstanding: 0,
  unallocatedCount: 0,
  unallocatedAmount: 0,
  fullyAllocatedCount: 0,
  fullyAllocatedAmount: 0,
  partiallyAllocatedCount: 0,
  partiallyAllocatedAmount: 0,
};

export function useReceiptsPageDataGraphQL(params?: GetReceiptsParams) {
  const listQuery = useReceiptsListGraphQL(params);
  const countQuery = useReceiptCountGraphQL(params);
  const totalsQuery = useReceiptTotalsGraphQL(params);

  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    receipts: listQuery.data?.receipts ?? [],
    pagination,
    totals: totalsQuery.data ?? defaultTotals,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
      totalsQuery.refetch();
    },
  };
}
